import { useEffect, useMemo, useState } from "react";
import AxiosInstance from "@/utils/AxiosInstance";
import { useAuth } from "@/contexts/use-auth";
import type { NursingNoteV2, PatientV2 } from "../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ClipboardList, Download, Eye, History, Trash } from "lucide-react";
import { formatDateTime12, getErrToast, getPromise, getPromiseToast } from "@/utils/helpers";
import { exportNursingNotesXlsx } from "@/utils/exportNursingNotesXlsx";
import { downloadBackendPdf } from "@/utils/downloadBackendPdf";
import { toast } from "sonner";
import TableRowCardWithSearch from "./TableRowCardWithSearch";
import { PatientV2ExpandableRowCard } from "./PatientV2ExpandableRowCard";
import { PatientV2CardSubtitle } from "./PatientV2CardSubtitle";
import { PatientV2ExpandableCardActions } from "./PatientV2ExpandableCardActions";
import { NursingNoteHistoryDialog } from "../nursingNotes/NursingNoteHistoryDialog";
import { useNursingNotesDateRange } from "../nursingNotes/useNursingNotesDateRange";
import {
  formatNursingCareTypeLabel,
  formatNursingProgramAcronym,
  formatNursingProgramLabel,
  formatNursingProgramTitle,
} from "../nursingNotes/nursingNoteConstants";
import {
  patientV2BlueOutlineBadgeClass,
  patientV2InvalidBadgeClass,
  patientV2SoftBadgeClass,
} from "../constants";

const NURSING_NOTE_COLUMNS = [
  { id: "program", label: "Program", isSortable: true },
  { id: "dateTime", label: "Date & Time", isSortable: true },
  { id: "authorName", label: "Author", isSortable: true },
  { id: "content", label: "Notes", isSortable: false },
] as const;

export function NursingNotesTab({
  patientId,
  patient,
  notes,
  listRefreshKey = 0,
  onOpenNursingNote,
}: {
  patientId: string;
  patient: PatientV2 | null;
  notes: NursingNoteV2[];
  listRefreshKey?: number;
  onOpenNursingNote?: (mode: "edit" | "view", note: NursingNoteV2) => void;
}) {
  const { user, profile, isAuthorized, hasModuleAdminAccess } = useAuth();
  const [rows, setRows] = useState<NursingNoteV2[]>(notes || []);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedNote, setSelectedNote] = useState<NursingNoteV2 | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<NursingNoteV2 | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [pdfExportLoading, setPdfExportLoading] = useState(false);

  const { nursingFilters, setNursingFilters, nursingDateRangeToolbar } = useNursingNotesDateRange();

  const userRole = String((user as { role?: string } | null)?.role ?? "").toLowerCase();
  const managerScope = String((user as { managerScope?: string } | null)?.managerScope ?? "");
  const isProviderOrPractitioner = userRole === "provider" || userRole === "practitioner";
  const isProviderOrPractitionerManager =
    userRole === "manager" && (managerScope === "provider" || managerScope === "practitioner");
  const isCaretaker = userRole === "caretaker";
  const profileId = String((profile as { _id?: string } | null)?._id ?? "");
  const assignedCaretakerIdList = (patient?.assignedCaretakerIds ?? []).map((caretaker) =>
    typeof caretaker === "string" ? caretaker : caretaker?._id,
  );
  const isCaretakerAssignedToPatient =
    !isCaretaker || (!!profileId && assignedCaretakerIdList.includes(profileId));
  const canManageNotes =
    !isProviderOrPractitioner &&
    !isProviderOrPractitionerManager &&
    (!isCaretaker || isCaretakerAssignedToPatient);
  useEffect(() => {
    if (!listRefreshKey) return;
    setRefreshKey((k) => k + 1);
  }, [listRefreshKey]);

  const listParams = useMemo(
    () => ({ patientId, refreshKey }),
    [patientId, refreshKey],
  );

  const mappedViewData = useMemo(
    () =>
      (rows || []).map((v: Record<string, unknown>) => ({
        ...v,
        program: v?.program ? String(v.program).toUpperCase() : "",
        programLabel: formatNursingProgramLabel(v?.program),
        programTitle: formatNursingProgramTitle(v?.program),
        programAcronym: formatNursingProgramAcronym(v?.program),
        nursingNoteInvalidStatus: v?.invalid,
        dateTime:
          v?.dateTimeLocal || v?.dateTime
            ? formatDateTime12(String(v?.dateTimeLocal || v?.dateTime))
            : "",
        authorName: String((v?.createdBy as { name?: string } | undefined)?.name || v?.authorName || ""),
        content: String(v?.content ?? v?.notes ?? v?.note ?? ""),
        noteType: String(v?.note_type || ""),
        careType: Array.isArray(v?.careType)
          ? v.careType.join(", ")
          : String(v?.careType ?? ""),
        careTypeLabel: formatNursingCareTypeLabel(v?.careType),
        duration:
          v?.duration != null && String(v.duration).trim() !== ""
            ? `${v.duration} min`
            : "—",
      })),
    [rows],
  );

  const openNoteDialog = (note: NursingNoteV2, mode: "edit" | "view") => {
    onOpenNursingNote?.(mode, note);
  };

  const canDeleteNote = (note: NursingNoteV2) => {
    const isAdmin = hasModuleAdminAccess("canAccessPatients");
    const createdBy = (note as Record<string, unknown>)?.createdBy as { _id?: string } | undefined;
    const createdById = String(createdBy?._id ?? "");
    const isCreator =
      String((user as { _id?: string } | null)?._id ?? "") === createdById ||
      profileId === createdById;
    return (
      isAdmin ||
      (isCaretakerAssignedToPatient && isAuthorized("canAddNursingNotes") && isCreator)
    );
  };

  const handleDownloadXlsx = async () => {
    if (!patientId || !patient) {
      toast.error("Patient data is not loaded yet.");
      return;
    }
    setExportLoading(true);
    try {
      const { data: res } = await AxiosInstance().get("/patients/nursing-note", {
        params: {
          patientId,
          page: 1,
          limit: 10000,
          skip: 0,
          ...nursingFilters,
        },
      });
      const exportNotes = (res as { data?: { notes?: NursingNoteV2[] }; notes?: NursingNoteV2[] })?.data
        ?.notes ?? (res as { notes?: NursingNoteV2[] })?.notes ?? [];
      exportNursingNotesXlsx(patient, exportNotes, `nursing-notes-${patientId}`);
      toast.success("Nursing notes downloaded");
    } catch {
      toast.error("Could not export nursing notes");
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!patientId) {
      toast.error("Patient data is not loaded yet.");
      return;
    }
    setPdfExportLoading(true);
    try {
      await downloadBackendPdf({
        url: "/patients/nursing-note/pdf",
        params: {
          patientId,
          ...nursingFilters,
        },
        fallbackFilename: `nursing-notes-${patientId}.pdf`,
      });
      toast.success("Nursing notes downloaded");
    } catch (error) {
      getErrToast(error);
    } finally {
      setPdfExportLoading(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete?._id) return;
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);
    try {
      await AxiosInstance().delete(`/patients/nursing-note/${noteToDelete._id}`);
      setRows((prev) => prev.filter((n) => String(n._id) !== String(noteToDelete._id)));
      setDeleteOpen(false);
      setNoteToDelete(null);
      resolveFn("Nursing note deleted successfully");
    } catch (error) {
      rejectFn(error);
    }
  };

  const headerActions = (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={exportLoading || pdfExportLoading || !patient}
        onClick={() => void handleDownloadXlsx()}
      >
        <Download className="mr-2 h-4 w-4" />
        {exportLoading ? "Exporting…" : "XLSX"}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={exportLoading || pdfExportLoading || !patient}
        onClick={() => void handleDownloadPdf()}
      >
        <Download className="mr-2 h-4 w-4" />
        {pdfExportLoading ? "Exporting…" : "PDF"}
      </Button>
    </div>
  );

  return (
    <div className="teachers">
      <div className="mb-3 flex flex-col gap-3 px-3 sm:flex-row sm:items-center sm:justify-end">
        {/* <h2 className="teachers flex items-center gap-2 text-xl font-bold">Nursing Notes</h2> */}
        {headerActions}
      </div>

      <TableRowCardWithSearch
        heading="Nursing Notes"
        url="/patients/nursing-note"
        params={listParams}
        apiDataKey="notes"
        fetchCondition={!!patientId}
        isIncludeSearch={false}
        isIgnoreSearchParams
        searchPlaceholder=""
        columnHeaders={NURSING_NOTE_COLUMNS as never}
        data={rows as never[]}
        viewData={mappedViewData}
        setData={setRows}
        filtersOptions={[]}
        filters={nursingFilters}
        setFilters={setNursingFilters}
        clickRow={() => {}}
        isActionColumn={canManageNotes}
        handleAction={(note: NursingNoteV2) => {
          if (!canManageNotes) return;
          openNoteDialog(note, "edit");
        }}
        moreActions={
          canManageNotes
            ? [
                {
                  label: "View",
                  icon: <Eye className="h-4 w-4" />,
                  isShow: () => true,
                  onClick: (note: NursingNoteV2) => openNoteDialog(note, "view"),
                },
                {
                  label: "History",
                  icon: <History className="h-4 w-4" />,
                  isShow: (note: NursingNoteV2) =>
                    Array.isArray((note as Record<string, unknown>)?.editHistory) &&
                    ((note as Record<string, unknown>).editHistory as unknown[]).length > 0,
                  onClick: (note: NursingNoteV2) => {
                    setSelectedNote(note);
                    setHistoryOpen(true);
                  },
                },
                {
                  label: "Delete",
                  icon: <Trash className="h-4 w-4" />,
                  isShow: (note: NursingNoteV2) => canDeleteNote(note),
                  onClick: (note: NursingNoteV2) => {
                    setNoteToDelete(note);
                    setDeleteOpen(true);
                  },
                },
              ]
            : []
        }
        headerExtraControls={nursingDateRangeToolbar}
        key={`nursing-cards-${patientId}-${refreshKey}`}
        renderRowCard={({ viewRow, row, actions, onPrimaryAction }) => {
          const v = viewRow as Record<string, unknown>;
          const programTitle = String(
            v?.programTitle || formatNursingProgramTitle(v?.program),
          ).trim();
          const programAcronym = String(
            v?.programAcronym || formatNursingProgramAcronym(v?.program),
          ).trim();
          const nursingNoteInvalidStatus = v?.nursingNoteInvalidStatus ? 'Invalid' : '';
          const authorName = String(v?.authorName || "Unknown").trim();
          const content = String(v?.content || "—");
          const noteType = String(v?.noteType || "").trim();
          const careTypeLabel = String(
            v?.careTypeLabel || formatNursingCareTypeLabel(v?.careType),
          ).trim();
          const duration = String(v?.duration || "").trim();
          const dateTime = String(v?.dateTime || "—");

          const subtitleParts = [
            ...(careTypeLabel ? [{ label: "Care", value: careTypeLabel }] : []),
            { label: "Author", value: authorName },
          ];

          const noteText = content === "—" ? "" : content;
          const isLongNote =
            noteText.length > 100 || noteText.split(/\s+/).filter(Boolean).length > 18;

          const details = [
            {
              label: "Notes",
              value: content,
              fullWidth: isLongNote,
              lineClamp: isLongNote ? 2 : undefined,
              wrap: !isLongNote,
            },
            ...(noteType ? [{ label: "Note Type", value: noteType }] : []),
            { label: "Date & Time", value: dateTime },
          ];

          return (
            <PatientV2ExpandableRowCard
              icon={<ClipboardList className="h-5 w-5 text-muted-foreground" />}
              title={programTitle || "Nursing Note"}
              subtitle={<PatientV2CardSubtitle parts={subtitleParts} />}
              headerBadges={
                <>
                  {programAcronym ? (
                    <Badge className={`${patientV2SoftBadgeClass} uppercase`}>
                      {programAcronym}
                    </Badge>
                  ) : null}
                  {duration && duration !== "—" ? (
                    <Badge
                      variant="outline"
                      className={`${patientV2BlueOutlineBadgeClass} font-semibold`}
                    >
                      {duration}
                    </Badge>
                  ) : null}
                   {nursingNoteInvalidStatus ? (
                    <Badge className={`${patientV2InvalidBadgeClass} uppercase`}>
                      {nursingNoteInvalidStatus}
                    </Badge>
                  ) : null}
                </>
              }
              details={details}
              actions={
                canManageNotes ? (
                  <PatientV2ExpandableCardActions
                    actions={actions}
                    row={row}
                    onPrimaryAction={onPrimaryAction}
                  />
                ) : undefined
              }
            />
          );
        }}
      />

      <NursingNoteHistoryDialog
        open={historyOpen}
        onOpenChange={(open) => {
          setHistoryOpen(open);
          if (!open) setSelectedNote(null);
        }}
        note={selectedNote}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Nursing Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this nursing note? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDeleteNote()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
